import { pick, values as _values } from 'lodash';
import React, { useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { selectActivitiesInContext } from 'app/containers/Activities/selectors';
import BackButton from '../../../components/button/NavigationHeaderButton/NavigationHeaderButton';
import { throwSubmissionErrorIfNecessary } from '../../../validations/common/reduxFormValidation';
import { updateAct } from '../../Act/actions';
import { selectLoggedUser } from '../../Authentication/selectors';
import LoadPreferences from '../../LoadPreferences/LoadPreferences';
import ActForm from '../ActForm';
import { actFormAdminFields, actFormFields } from '../constants';
import Act from '../../../../shared/domain/activity/act/model/Act';
import AuthUserDomain from '../../../../shared/domain/authentication/AuthUser';
import Permission from '../../../../shared/domain/authorization/model/Permission';
import { selectUserProfileInContext } from '../../User/selectors';
import User from '../../../../shared/domain/user/User';
import { validator } from '../../../validations/act/actValidation';
import ActivityStatus from '../../../../shared/core/activity/domain/ActivityStatus';
import { usePostHog } from 'posthog-js/react';

export interface OwnProps {
  act?: Act;
  formClasses: any;
}

export interface ReduxStateProps {
  loggedUser: AuthUserDomain;
  impersonatedUser: User;
  activities: any[];
}

export interface Props extends OwnProps, ReduxStateProps {}

const ModifyAct = ({ act: actProp, activities, loggedUser, formClasses, impersonatedUser }: Props) => {
  const location = useLocation();
  const { activityId } = useParams();
  const posthog = usePostHog();
  const formOpenAt = useRef<number | null>(null);
  const act = useMemo(
    () => actProp ?? activities.find((activity) => activity.id === activityId),
    [actProp, activities, activityId]
  );

  // Start measuring when component mounts
  useEffect(() => {
    formOpenAt.current = performance.now();
  }, []);

  const navigate = useNavigate();

  const onSent = (originalAct: Act) => (values, dispatch) => {
    const updatedValues = {
      ...values,
      status: ActivityStatus.SENT
    };
    validateAndSaveAct(originalAct, updatedValues, impersonatedUser, dispatch);
  };

  const onWait = (originalAct: Act) => (values, dispatch) => {
    const updatedValues = {
      ...values,
      status: ActivityStatus.WAITING
    };
    validateAndSaveAct(originalAct, updatedValues, impersonatedUser, dispatch);
  };

  const onSave = (originalAct: Act) => (values, dispatch) => {
    validateAndSaveAct(originalAct, values, impersonatedUser, dispatch);
  };

  const onCancel = (originalAct: Act) => (values, dispatch) => {
    const updatedValues = {
      ...values,
      status: ActivityStatus.CANCELED
    };
    validateAndSaveAct(originalAct, updatedValues, impersonatedUser, dispatch);
  };

  const isBillNumberMapEmpty = (): boolean => !act.billNumberMap || Object.keys(act.billNumberMap).length === 0;

  const getDisplayableFieldOnly = (act, loggedUser: AuthUserDomain) => {
    if (loggedUser.canSome(Permission.DO_ADMIN_ACTIONS, Permission.DO_AGENT_ACTIONS)) {
      return pick(act, _values(actFormAdminFields));
    }
    return pick(act, _values(actFormFields));
  };

  const validateAndSaveAct = (originalAct: Act, updatedValues: Partial<Act>, impersonatedUser: User, dispatch: any) => {
    const updatedValuesWithOnlineStatus = { ...updatedValues };
    throwSubmissionErrorIfNecessary(
      validator.validateAct({ act: updatedValuesWithOnlineStatus, user: impersonatedUser })
    );
    dispatch(updateAct(originalAct, updatedValuesWithOnlineStatus));

    if (formOpenAt.current) {
      const formClosedAt = performance.now();
      const formOpenDuration = ((formClosedAt - formOpenAt.current) / 1000).toFixed(2);

      posthog.capture('actFormDuration', {
        duration: parseFloat(formOpenDuration),
        isNewAct: false,
        status: updatedValues.status
      });
    }
    navigate({ pathname: '../..', search: location.search });
  };
  return (
    <LoadPreferences extraIsLoading={!act}>
      <>
        <BackButton urlToGoOnClick="../.." position="left" preventRenderOnAdminConsole />
        <ActForm
          onSave={
            loggedUser.canSome(Permission.DO_ADMIN_ACTIONS, Permission.DO_AGENT_ACTIONS) ? onSave(act) : undefined
          }
          onCancel={
            loggedUser.canSome(Permission.DO_DOCTOR_ACTIONS) && act.status === ActivityStatus.NEED_FIX
              ? onCancel(act)
              : undefined
          }
          isBillNumberMapEmpty={isBillNumberMapEmpty}
          onSent={onSent(act)}
          onWait={onWait(act)}
          act={getDisplayableFieldOnly(act, loggedUser)}
          classes={formClasses}
        />
      </>
    </LoadPreferences>
  );
};

export const mapStateToProps = createStructuredSelector({
  loggedUser: selectLoggedUser(),
  impersonatedUser: selectUserProfileInContext(),
  activities: selectActivitiesInContext()
});

export default connect(mapStateToProps)(ModifyAct);
